import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { readStoredTextSize, TextSize } from '../utils/textSize';

// App state interface
interface AppState {
  user: {
    id: string | null;
    email: string | null;
    displayName: string | null;
    firstName?: string | null;
    lastName?: string | null;
    role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN' | null;
  } | null;
  isLoading: boolean;
  theme: 'light' | 'dark';
  textSize: TextSize;
  redirectUrl: string | null;
}

// Initial state
const initialState: AppState = {
  user: null,
  isLoading: false,
  theme: 'light',
  textSize: readStoredTextSize(),
  redirectUrl: null,
};

// Action types
type AppAction =
  | { type: 'SET_USER'; payload: AppState['user'] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'SET_TEXT_SIZE'; payload: TextSize }
  | { type: 'SET_REDIRECT_URL'; payload: string | null }
  | { type: 'LOGOUT' };

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'SET_TEXT_SIZE':
      return { ...state, textSize: action.payload };
    case 'SET_REDIRECT_URL':
      return { ...state, redirectUrl: action.payload };
    case 'LOGOUT':
      return { ...state, user: null, redirectUrl: null };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
